"use client";

/**
 * Auth Hook
 * 
 * Provides authentication state for client components.
 * In dev mode, returns the dev tenant automatically.
 */

import { createContext, useContext, type ReactNode } from "react";
import type { AuthState } from "../lib/auth-types";
import { DEV_AUTH_STATE } from "../lib/auth-types";
import { getEffectiveTimezone, DEFAULT_TIMEZONE } from "../lib/timezone";

// Auth context
const AuthContext = createContext<AuthState | null>(null);

/**
 * Auth Provider Props
 */
interface AuthProviderProps {
	children: ReactNode;
	initialState?: AuthState | null;
	allowDevModeFallback?: boolean;
}

/**
 * Auth Provider Component
 * Wrap your app with this to provide auth state.
 * SECURITY: Dev mode is only allowed in development builds.
 */
export function AuthProvider({ children, initialState, allowDevModeFallback = false }: AuthProviderProps) {
	// Dev mode only allowed in development — blocked in production builds.
	// The server request path decides whether dev auth is allowed for this host.
	const isDevMode =
		allowDevModeFallback &&
		process.env.NEXT_PUBLIC_DEV_MODE === "true" &&
		process.env.NODE_ENV !== "production";

	if (process.env.NEXT_PUBLIC_DEV_MODE === "true" && process.env.NODE_ENV === "production") {
		console.error(
			"[Auth] CRITICAL: NEXT_PUBLIC_DEV_MODE=true in production! Ignoring — using real session."
		);
	}

	const state = isDevMode ? DEV_AUTH_STATE : initialState;

	return (
		<AuthContext.Provider value={state || null}>
			{children}
		</AuthContext.Provider>
	);
}

/**
 * Hook to access auth state
 */
export function useAuth(): AuthState | null {
	return useContext(AuthContext);
}

/**
 * Hook to require auth state
 * Throws if not authenticated
 */
export function useRequireAuth(): AuthState {
	const auth = useAuth();
	if (!auth?.isAuthenticated) {
		throw new Error("Authentication required");
	}
	return auth;
}

/**
 * Hook to check if user is authenticated
 */
export function useIsAuthenticated(): boolean {
	const auth = useAuth();
	return auth?.isAuthenticated ?? false;
}

/**
 * Hook to get current user
 */
export function useUser() {
	const auth = useAuth();
	return auth?.user ?? null;
}

/**
 * Hook to get current organization
 */
export function useOrg() {
	const auth = useAuth();
	return auth?.org ?? null;
}

/**
 * Hook to get current brand
 */
export function useBrand() {
	const auth = useAuth();
	return auth?.brand ?? null;
}

/**
 * Hook to check if in dev mode
 */
export function useIsDevMode(): boolean {
	const auth = useAuth();
	return auth?.isDevMode ?? false;
}

/**
 * Hook to get the effective timezone for display
 * 
 * Priority:
 * 1. Brand timezone (if set)
 * 2. User timezone (if set)
 * 3. UTC fallback
 * 
 * @returns IANA timezone string (e.g., "America/New_York" or "UTC")
 * 
 * @example
 * ```tsx
 * const timezone = useTimezone();
 * const timeDisplay = formatTimeInTimezone(scheduledAt, timezone, { use24Hour: false });
 * ```
 */
export function useTimezone(): string {
	const auth = useAuth();
	return getEffectiveTimezone(
		auth?.user?.timezone,
		auth?.brand?.timezone
	);
}

/**
 * Hook to get user and brand timezone separately
 * Useful for settings pages where you need to display/edit both
 * 
 * @returns Object with userTimezone, brandTimezone, and effectiveTimezone
 */
export function useTimezoneDetails(): {
	userTimezone: string | null;
	brandTimezone: string | null;
	effectiveTimezone: string;
} {
	const auth = useAuth();
	const userTimezone = auth?.user?.timezone ?? null;
	const brandTimezone = auth?.brand?.timezone ?? null;
	const effectiveTimezone = getEffectiveTimezone(userTimezone, brandTimezone);

	return {
		userTimezone,
		brandTimezone,
		effectiveTimezone,
	};
}
